/**
 * Hash Utilities
 * Client-side file hashing for duplicate detection (P9-B)
 *
 * Uses Web Crypto API (native browser) for SHA-256 hashing
 * No external dependencies required
 */

/**
 * Compute SHA-256 hash of a string (source code content)
 * @param {string} content - The content to hash
 * @returns {Promise<string>} - Hex-encoded SHA-256 hash
 */
export const computeSHA256 = async (content) => {
    if (!content) {
        throw new Error('Content is required for hashing');
    }

    // Convert string to Uint8Array
    const encoder = new TextEncoder();
    const data = encoder.encode(content);

    // Use Web Crypto API for SHA-256
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);

    // Convert buffer to hex string
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

    return hashHex;
};

/**
 * Compute SHA-256 hash of a File object
 * @param {File} file - The file to hash
 * @returns {Promise<string>} - Hex-encoded SHA-256 hash
 */
export const computeFileHash = async (file) => {
    if (!file) {
        throw new Error('File is required for hashing');
    }

    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = async (event) => {
            try {
                const content = event.target.result;
                const hash = await computeSHA256(content);
                resolve(hash);
            } catch (error) {
                reject(error);
            }
        };

        reader.onerror = () => {
            reject(new Error('Failed to read file'));
        };

        reader.readAsText(file);
    });
};

/**
 * Compute SHA-256 hash of EA content (main source + includes)
 * Creates a deterministic hash that matches backend computation
 * @param {object} eaContent - EA content object from getEAContent
 * @param {string} eaContent.main_source - Main source code
 * @param {Object<string, string>} eaContent.includes - Include files (optional)
 * @returns {Promise<string>} - Hex-encoded SHA-256 hash
 */
export const computeEAContentHash = async (eaContent) => {
    if (!eaContent || !eaContent.main_source) {
        throw new Error('EA content with main_source is required');
    }

    // Normalize content for consistent hashing:
    // 1. Trim whitespace
    // 2. Normalize line endings to LF
    // 3. Concatenate main source with sorted includes
    let contentToHash = eaContent.main_source.trim().replace(/\r\n/g, '\n');

    // If includes exist, append them in sorted order (deterministic)
    if (eaContent.includes && typeof eaContent.includes === 'object') {
        const sortedKeys = Object.keys(eaContent.includes).sort();
        for (const key of sortedKeys) {
            const includeContent = eaContent.includes[key];
            if (includeContent) {
                contentToHash += '\n' + includeContent.trim().replace(/\r\n/g, '\n');
            }
        }
    }

    return computeSHA256(contentToHash);
};
